import React, { useState, useEffect } from 'react';
import { X, Wrench } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { API_URL } from '../../config';

const ModifyModal = ({ 
  isOpen, 
  onClose, 
  originalImage,
  originalPrompt,
  onModify,
  isGenerating
}) => {
  const [config, setConfig] = useState(null);
  const [selectedChanges, setSelectedChanges] = useState([]);
  const [customText, setCustomText] = useState('');
  const [numImages, setNumImages] = useState(1);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false); // NEU: Templates!
  
  // Lade Config aus Backend (V1)
  useEffect(() => {
    if (isOpen) {
      loadConfig();
    }
  }, [isOpen]);
  
  const loadConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/modes/public/modify/v1`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error loading modify config:', error);
    }
  };
  
  // Block body scroll


  useEffect(() => { if (isOpen) { // Lock scroll 
      const scrollY = window.scrollY; 
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed'; 
      document.body.style.top = `-${scrollY}px`; 
      document.body.style.width = '100%'; // Cleanup (unlock scroll) 
      return () => { 
        document.body.style.overflow = ''; 
        document.body.style.position = ''; 
        document.body.style.width = ''; 
        document.body.style.top = ''; 
        // Restore scroll position 
        window.scrollTo(0, scrollY); 
      }; }
     }, [isOpen]);
  
  if (!isOpen) return null;
  if (!config) return null;
  
  const handleModify = () => {
    // Baue finalen Prompt
    const changes = selectedChanges.map(change => change.prompt_addition).join(', ');
    const finalPrompt = customText?.trim() 
      ? `${originalPrompt}, ${changes}, ${customText}`
      : `${originalPrompt}, ${changes}`;
    
    onModify({
      prompt: finalPrompt,
      sourceImage: originalImage,
      num_images: numImages,
      modifications: selectedChanges.map(c => c.id)
    });
  };
  
  const toggleButton = (button) => {
    const isSelected = selectedChanges.some(c => c.id === button.id);
    if (isSelected) {
      setSelectedChanges(prev => prev.filter(c => c.id !== button.id));
    } else {
      setSelectedChanges(prev => [...prev, button]);
    }
  };
  
  // Dynamisches Icon-Rendering
  const DynamicIcon = ({ iconName, className }) => {
    const IconComponent = LucideIcons[iconName];
    if (!IconComponent) return <LucideIcons.Sparkles className={className} />;
    return <IconComponent className={className} />;
  };
  
  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999]"
        onClick={onClose}
        data-testid="modify-modal-overlay"
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-gradient-to-br from-[#0A0E1A] to-[#111827] backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto relative isolate">
          
          {/* Header - DYNAMISCH */}
          <div className="sticky top-0 bg-gradient-to-br from-white/10 to-transparent backdrop-blur-xl border-b border-white/10 p-6 flex items-center justify-between z-20">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-yellow-500/20 backdrop-blur border border-orange-500/30 rounded-2xl flex items-center justify-center">
                <DynamicIcon iconName={config.header_icon} className="w-6 h-6 text-orange-400 drop-shadow-[0_0_8px_rgba(251,146,60,0.6)]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{config.header_title}</h2>
                <p className="text-sm text-orange-400/80">{config.header_subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors duration-150"
              data-testid="modify-modal-close-button"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-6">
            
            {/* Original Image */}
            <div>
              <h3 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">{config.original_image_label}</h3>
              <div className="relative rounded-xl overflow-hidden border-2 border-white/20 max-w-md mx-auto">
                <img 
                  src={originalImage} 
                  alt="Original" 
                  className="w-full h-auto"
                />
              </div>
            </div>
            
            {/* Schnellauswahl - DYNAMISCHE KATEGORIEN */}
            <div>
              <h3 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">Schnellauswahl:</h3>
              
              <div className="space-y-4">
                {config.categories
                  ?.filter(cat => cat.visible !== false)
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((category) => (
                    <div key={category.id}>
                      <h4 
                        className="text-sm font-bold mb-2 uppercase tracking-wider flex items-center gap-2"
                        style={{ color: category.label_color || '#06B6D4' }}
                      >
                        {category.icon && <DynamicIcon iconName={category.icon} className="w-4 h-4" />}
                        {category.label}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {category.buttons
                          ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((button) => {
                            const isSelected = selectedChanges.some(c => c.id === button.id);
                            return (
                              <button
                                key={button.id}
                                onClick={() => toggleButton(button)}
                                className={`px-4 py-2.5 rounded-full font-medium text-sm transition-all ${
                                  isSelected
                                    ? 'bg-[#10B981] text-black border-2 border-[#10B981]'
                                    : 'bg-white/5 text-white/80 border border-white/20 hover:bg-white/10'
                                }`}
                                data-testid={`modify-btn-${button.id}`}
                              >
                                {button.icon && <DynamicIcon iconName={button.icon} className="w-4 h-4 inline mr-1.5" />}
                                {button.label}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            
            {/* Freitext */}
            <div>
              <h3 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">{config.freetext_label}</h3>
              
              {/* Vorlagen Button - NEU! */}
              <button
                onClick={() => setShowTemplatesModal(true)}
                className="w-full py-4 px-5 bg-gradient-to-br from-white/10 to-white/5 hover:from-orange-500/20 hover:to-yellow-500/10 backdrop-blur-sm border border-orange-500/30 hover:border-orange-500/50 rounded-xl font-semibold text-white text-base transition-all flex items-center justify-center gap-2 shadow-lg mb-3"
                data-testid="modify-templates-button"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                {config.templates_button_text}
              </button>
              
              <textarea
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder={config.freetext_placeholder}
                className="w-full px-4 py-3 bg-white/5 backdrop-blur-sm border-2 border-white/20 rounded-xl text-white placeholder:text-white/40 focus:border-orange-400 focus:outline-none transition-colors duration-150 resize-none shadow-lg"
                rows={3}
                data-testid="modify-custom-input"
              />
            </div>
            
            {/* Info-Box */}
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
              <p className="text-orange-400 text-sm text-center font-semibold mb-2">
                {config.info_title}
              </p>
              <p className="text-orange-300/80 text-xs text-center">
                {config.info_text}
              </p>
            </div>
            
            {/* Anzahl Bilder */}
            <div>
              <h3 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">{config.num_images_label}</h3>
              <select
                value={numImages}
                onChange={(e) => setNumImages(parseInt(e.target.value))}
                className="w-full px-5 py-3 bg-white/10 backdrop-blur border border-white/20 rounded-xl text-white font-medium focus:border-orange-400/50 focus:outline-none transition-colors duration-150 cursor-pointer"
              >
                {(config.num_images_options || [1, 2, 3, 4]).map(num => (
                  <option key={num} value={num} className="bg-[#1A1A1A]">{num} {num === 1 ? 'Bild' : 'Bilder'}</option>
                ))}
              </select>
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-4 px-6 rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/20 font-bold transition-all"
              >
                {config.button_cancel_text}
              </button>
              
              <button
                onClick={handleModify}
                disabled={isGenerating || (selectedChanges.length === 0 && !customText?.trim())}
                className={`flex-1 py-4 px-6 rounded-full transition-all duration-200 font-bold flex items-center justify-center gap-3 ${
                  isGenerating || (selectedChanges.length === 0 && !customText?.trim())
                    ? 'bg-white/10 text-white/40 cursor-not-allowed border border-white/10'
                    : 'bg-gradient-to-r from-orange-400 via-orange-500 to-yellow-500 text-black hover:shadow-2xl hover:shadow-orange-500/60 hover:scale-[1.02] shadow-xl shadow-orange-500/50 border-2 border-orange-400/50'
                }`}
                data-testid="modify-generate-button"
              >
                {isGenerating ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{config.button_generating_text}</span>
                  </>
                ) : (
                  <>
                    <Wrench className="w-5 h-5" />
                    <span>{config.button_generate_text}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Templates Modal - GLEICHER STYLE wie Remix */}
      {showTemplatesModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
          style={{ 
            position: 'fixed',
            overflow: 'hidden',
            touchAction: 'none'
          }}
        >
          <div className="bg-gradient-to-br from-[#0A0E1A] to-[#111827] border border-white/20 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-br from-white/10 to-transparent backdrop-blur-xl border-b border-white/10 p-6 flex items-center justify-between z-20">
              <div>
                <h3 className="text-2xl font-bold text-white mb-1">{config.templates_modal_title}</h3>
                <p className="text-gray-400 text-sm">{config.templates?.length || 0} {config.templates_modal_subtitle}</p>
              </div>
              <button
                onClick={() => setShowTemplatesModal(false)}
                className="w-10 h-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center hover:bg-white/20 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {/* Handle-Strich */}
            <div className="w-16 h-1 bg-white/20 rounded-full mx-auto mt-6"></div>
            
            {/* Content */}
            <div className="p-6">
              
              {/* Info Banner */}
              <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl mb-6">
                <p className="text-orange-300 text-sm">
                  {config.templates_info_text}
                </p>
              </div>
              
              {/* Templates Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(config.templates || [])
                  .sort((a, b) => a.order - b.order)
                  .map((template) => (
                    <button
                      key={template.id}
                      onClick={() => {
                        setCustomText(template.full);
                        setShowTemplatesModal(false);
                      }}
                      className="p-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl hover:bg-white/10 hover:border-orange-400/50 transition-all text-left group"
                      data-testid={`modify-template-${template.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-orange-500/30 transition-all">
                          <Wrench className="w-4 h-4 text-orange-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm mb-1 line-clamp-1">{template.short}</p>
                          <p className="text-gray-400 text-xs line-clamp-2">{template.full}</p>
                          {template.category && (
                            <span className="inline-block mt-2 px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                              {template.category}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
              
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModifyModal;
